import React, { useEffect, useState, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { TrendingUp, Lightbulb, Copy, Check, AlertCircle, FileText, ArrowRight, ChevronUp, ChevronDown, Layers, Database, Search, Sparkles, Info, X, Loader2, BookOpen, Volume2, Square } from "lucide-react";
import StreamViewer from "../StreamViewer/StreamViewer";
import ExpandModal from "./ExpandModal";
import { BASEURL, ERRORMESSAGES, ERROR_MESSAGE_TIMEOUT, STORAGE_KEYS } from "../../utils/constants";
import { apiRequest, getUserDetails } from "../../utils/authUtils";
import { useTextToSpeech } from "../../hooks/useTextToSpeech";
import {
  setTabs,
  setLoadingTabs,
  setActiveTab,
  setInsightsData,
  setLoading,
  setError,
  setRefreshing,
  clearError,
} from "../../utils/insightsSlice";

const formatCount = (num) => {
  if (num >= 1_000_000_000) return (num / 1_000_000_000).toFixed(1).replace(/\.0$/, "") + "B";
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  if (num >= 1_000) return (num / 1_000).toFixed(1).replace(/\.0$/, "") + "K";
  return num.toString();
};

const InsightsDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { tabs, activeTab, data, loading, loadingTabs, refreshing } = useSelector(
    (state) => state.insights
  );

  const [errorMessage, setErrorMessage] = useState("");
  const [copiedSection, setCopiedSection] = useState(null);
  const [isReportExpanded, setIsReportExpanded] = useState(false);
  const [activeReportTab, setActiveReportTab] = useState(0);
  const [reportContent, setReportContent] = useState("");
  const [loadingReport, setLoadingReport] = useState(false);
  const reportSectionRef = useRef(null);
  const [showFrameworksModal, setShowFrameworksModal] = useState(false);
  const [showSourcesModal, setShowSourcesModal] = useState(false);
  const [showMoreTabsModal, setShowMoreTabsModal] = useState(false);
  const [visibleTabs, setVisibleTabs] = useState([]);
  const [hiddenTabs, setHiddenTabs] = useState([]);
  const tabNavigationRef = useRef(null);
  const moreTabsButtonRef = useRef(null);
  const sourcesExpandRef = useRef(null);
  const datasourcesItemsRef = useRef(null);
  const mobileFrameworksButtonRef = useRef(null);
  const mobileDatabaseButtonRef = useRef(null);
  const [visibleSourcesCount, setVisibleSourcesCount] = useState(null);
  const currentTabData = data[activeTab];
  const allSources = currentTabData?.metadata?.dataSources || [];
  const reportContentRef = useRef(null);
  const userDetails = useMemo(() => getUserDetails(), []);
  const { textToSpeech, ttsState, stopSpeech } = useTextToSpeech(
    userDetails?.tts_info?.token, userDetails?.tts_info?.region
  );

  useEffect(() => { fetchTabsConfiguration(); }, []);
  useEffect(() => { if (activeTab) fetchInsightsData(activeTab); }, [activeTab]);
  useEffect(() => { setActiveReportTab(0); }, [activeTab]);

  useEffect(() => {
    if (errorMessage) {
      const timeout = setTimeout(() => { setErrorMessage(""); dispatch(clearError()); }, ERROR_MESSAGE_TIMEOUT);
      return () => clearTimeout(timeout);
    }
  }, [errorMessage, dispatch]);

  useEffect(() => { setVisibleSourcesCount(null); }, [currentTabData?.metadata?.dataSources]);

  useEffect(() => {
    const container = datasourcesItemsRef.current;
    if (!container || allSources.length === 0 || visibleSourcesCount !== null) return;
    const measureVisibleItems = () => {
      const containerRect = container.getBoundingClientRect();
      const items = container.querySelectorAll('[data-source-item]');
      let count = 0;
      for (const item of items) {
        if (item.getBoundingClientRect().right <= containerRect.right + 1) count++;
        else break;
      }
      setVisibleSourcesCount(Math.max(count, 1));
    };
    const timeoutId = setTimeout(measureVisibleItems, 50);
    return () => clearTimeout(timeoutId);
  }, [visibleSourcesCount, allSources]);

  useEffect(() => {
    const handleResize = () => setVisibleSourcesCount(null);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchTabsConfiguration = async () => {
    dispatch(setLoadingTabs(true));
    try {
      const response = await apiRequest(`${BASEURL}/api/conversations/insights/dashboard`);
      if (response.status === 401) {
        localStorage.removeItem(STORAGE_KEYS.SESSION);
        sessionStorage.setItem(STORAGE_KEYS.LOGOUT_FLAG, "true");
        navigate("/login");
        return;
      }
      if (!response.ok) throw new Error("Failed to fetch tabs configuration");
      const result = await response.json();
      dispatch(setTabs({ tabs: result.tabs, defaultTab: result.defaultTab }));
    } catch (err) {
      const errorMsg = err.message || "Failed to load tabs configuration";
      dispatch(setError(errorMsg));
      setErrorMessage(errorMsg);
      dispatch(setLoadingTabs(false));
    }
  };

  const fetchInsightsData = async (tab, forceRefresh = false) => {
    if (data[tab] && !forceRefresh && !refreshing) return;
    dispatch(setLoading(true));
    try {
      const response = await apiRequest(`${BASEURL}/api/conversations/insights/dashboard?tab=${tab}`);
      if (response.status === 401) {
        localStorage.removeItem(STORAGE_KEYS.SESSION);
        sessionStorage.setItem(STORAGE_KEYS.LOGOUT_FLAG, "true");
        navigate("/login");
        return;
      }
      if (!response.ok) throw new Error("Failed to fetch insights data");
      const result = await response.json();
      dispatch(setInsightsData({ tab, data: result }));
    } catch (err) {
      const errorMsg = err.message || ERRORMESSAGES.ERRORFETCHINGPASTACTIVITIES;
      dispatch(setError(errorMsg));
      setErrorMessage(errorMsg);
    }
  };

  const handleTabChange = (tab) => { stopSpeech(); dispatch(setActiveTab(tab)); };

  const handleRefresh = async () => {
    dispatch(setRefreshing(true));
    await fetchInsightsData(activeTab, true);
    dispatch(setRefreshing(false));
  };

  const handleReadFullReport = () => {
    if (currentTabData?.fullReportUrl?.length > 0) {
      const newExpanded = !isReportExpanded;
      if (!newExpanded) stopSpeech();
      setIsReportExpanded(newExpanded);
      if (newExpanded) {
        setActiveReportTab(0);
        setTimeout(() => reportSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
      }
    }
  };

  const handleReportTabChange = (index) => { stopSpeech(); setActiveReportTab(index); };
  const getTimeAgo = () => currentTabData?.lastUpdated || "Just now";

  const escapeHtml = (text) => {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  };

  const copyAllToClipboard = async (items, sectionId) => {
    try {
      const plainText = items.map((item, i) => `${i + 1}. ${item.title}`).join('\n');
      const htmlText = `<div>${items.map((item, i) => `<div><strong>${i + 1}.</strong> ${escapeHtml(item.title)}</div>`).join('')}</div>`;
      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([new ClipboardItem({
          'text/html': new Blob([htmlText], { type: 'text/html' }),
          'text/plain': new Blob([plainText], { type: 'text/plain' })
        })]);
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(plainText);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = plainText;
        textarea.style.cssText = 'position:fixed;opacity:0;left:-9999px';
        document.body.appendChild(textarea);
        try { textarea.select(); document.execCommand('copy'); } finally { document.body.removeChild(textarea); }
      }
      setCopiedSection(`${sectionId}-success`);
      setTimeout(() => setCopiedSection(null), 500);
    } catch (err) {
      setCopiedSection(`${sectionId}-error`);
      setTimeout(() => setCopiedSection(null), 1000);
    }
  };

  const copyReportToClipboard = async () => {
    const container = reportContentRef.current;
    if (!container) return;
    try {
      const cloned = container.cloneNode(true);
      cloned.querySelectorAll('.stream-action-btn, .canvas-error-block').forEach(el => el.remove());
      const html = cloned.innerHTML;
      const text = cloned.innerText || cloned.textContent || '';
      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([text], { type: 'text/plain' })
        })]);
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.cssText = 'position:fixed;opacity:0;left:-9999px';
        document.body.appendChild(textarea);
        try { textarea.select(); document.execCommand('copy'); } finally { document.body.removeChild(textarea); }
      }
      setCopiedSection('report-success');
      setTimeout(() => setCopiedSection(prev => prev === 'report-success' ? null : prev), 500);
    } catch (err) {
      setCopiedSection('report-error');
      setTimeout(() => setCopiedSection(prev => prev === 'report-error' ? null : prev), 1000);
    }
  };

  const handleReportTTS = (e) => {
    if (ttsState === 'playing' || ttsState === 'loading') { stopSpeech(); return; }
    const container = reportContentRef.current;
    if (!container) return;
    const text = (container.innerText || container.textContent || '')
      .replace(/[*#_`]/g, '').replace(/\s+/g, ' ').trim();
    if (text.length > 0) textToSpeech(text, e.currentTarget);
  };

  useEffect(() => {
    const calculateVisibleTabs = () => {
      if (!tabNavigationRef.current || tabs.length === 0) { setVisibleTabs(tabs); setHiddenTabs([]); return; }
      const containerWidth = tabNavigationRef.current.offsetWidth;
      const moreButtonWidth = 0;
      const TAB_GAP = 8, CHAR_WIDTH = 8, TAB_PADDING = 32;
      const getTabWidth = (tab) => (tab.label.length * CHAR_WIDTH) + TAB_PADDING + TAB_GAP;
      const totalWidth = tabs.reduce((sum, tab) => sum + getTabWidth(tab), 0);
      if (totalWidth <= containerWidth) { setVisibleTabs(tabs); setHiddenTabs([]); return; }
      const availableWidth = containerWidth - moreButtonWidth;
      const activeTabIndex = tabs.findIndex(tab => tab.key === activeTab);
      const visibleIndices = new Set();
      let currentWidth = 0;
      if (activeTabIndex >= 0) { visibleIndices.add(activeTabIndex); currentWidth = getTabWidth(tabs[activeTabIndex]); }
      for (let i = 0; i < tabs.length; i++) {
        if (visibleIndices.has(i)) continue;
        const tabWidth = getTabWidth(tabs[i]);
        if (currentWidth + tabWidth <= availableWidth) { visibleIndices.add(i); currentWidth += tabWidth; }
      }
      if (visibleIndices.size === 0 && tabs.length > 0) visibleIndices.add(activeTabIndex >= 0 ? activeTabIndex : 0);
      setVisibleTabs(tabs.filter((_, i) => visibleIndices.has(i)));
      setHiddenTabs(tabs.filter((_, i) => !visibleIndices.has(i)));
    };
    const timeoutId = setTimeout(calculateVisibleTabs, 100);
    window.addEventListener('resize', calculateVisibleTabs);
    return () => { clearTimeout(timeoutId); window.removeEventListener('resize', calculateVisibleTabs); };
  }, [tabs, activeTab]);

  return (
    <div className="flex flex-col bg-[var(--bg-secondary)] h-full">
      {/* Tab Navigation Header — hidden when no tabs are configured */}
      {(loadingTabs || tabs.length > 0) && <div className="sticky top-0 z-10 bg-[var(--bg-primary)] border-b border-[var(--border-color)] px-2 sm:px-4 md:px-6 py-1.5 sm:py-3">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 overflow-x-auto flex-1 min-w-0 hide-scrollbar" ref={tabNavigationRef}>
            {loadingTabs ? (
              <div className="flex items-center gap-2 text-[var(--text-secondary)] text-sm">
                <Loader2 size={16} className="animate-spin" />
                Loading tabs...
              </div>
            ) : visibleTabs.length > 0 ? (
              visibleTabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => handleTabChange(tab.key)}
                  title={tab.label}
                  className={`px-3 py-1 sm:px-4 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all shrink-0
                    max-w-[140px] sm:max-w-none truncate sm:whitespace-nowrap
                    ${activeTab === tab.key
                      ? 'bg-brand-500 text-white shadow-sm'
                      : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)]'
                    }`}
                >
                  {tab.label}
                </button>
              ))
            ) : (
              <p className="text-[var(--text-secondary)] text-sm">No tabs available</p>
            )}
          </div>

          {hiddenTabs.length > 0 && (
            <button
              ref={moreTabsButtonRef}
              onClick={() => setShowMoreTabsModal(true)}
              className="p-1.5 rounded-full text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] transition-colors shrink-0"
              aria-label="More tabs"
            >
              <ChevronDown size={16} />
            </button>
          )}

          <span className="text-xs text-[var(--text-tertiary)] whitespace-nowrap shrink-0">
            <span className="hidden sm:inline">Updated </span>{getTimeAgo()}
          </span>
        </div>
      </div>}

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto px-2 sm:px-4 md:px-6 py-5 pb-20">
        {/* Error */}
        {errorMessage && (
          <div className="flex items-start gap-3 p-4 mb-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-xl">
            <AlertCircle size={18} className="text-red-500 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-red-700 dark:text-red-400 text-sm">Error Loading Insights</p>
              <p className="text-red-600 dark:text-red-400 text-sm mt-1">{errorMessage}</p>
            </div>
            <button onClick={() => setErrorMessage("")} className="text-red-400 hover:text-red-600">
              <X size={16} />
            </button>
          </div>
        )}

        {/* Loading */}
        {loading && !currentTabData ? (
          <div className="flex flex-col items-center justify-center py-20 text-[var(--text-secondary)]">
            <Loader2 size={40} className="animate-spin text-brand-500 mb-4" />
            <p className="text-sm">Loading insights...</p>
          </div>
        ) : currentTabData ? (
          <>
            {/* Key Highlights */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <TrendingUp size={18} className="text-brand-500" />
                  <h2 className="text-lg font-semibold text-[var(--text-primary)] m-0">Key Highlights</h2>
                </div>
                <button
                  onClick={() => copyAllToClipboard(currentTabData.keyHighlights || [], 'highlights')}
                  className={`p-2 rounded-lg transition-colors ${
                    copiedSection === 'highlights-success' ? 'text-green-500 bg-green-50 dark:bg-green-950/30' :
                    copiedSection === 'highlights-error' ? 'text-red-500' :
                    'text-[var(--text-tertiary)] hover:text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)]'
                  }`}
                  title="Copy all highlights"
                >
                  {copiedSection === 'highlights-success' ? <Check size={16} /> : <Copy size={16} />}
                </button>
              </div>
              <div className="bg-[var(--card-bg)] border border-[var(--border-color)] rounded-xl overflow-hidden">
                {currentTabData.keyHighlights?.map((highlight, index) => (
                  <div key={highlight.id} className="flex items-start gap-3 px-4 py-2.5 md:px-5 md:py-3">
                    <span className="flex items-center justify-center w-5 h-5 rounded-full bg-brand-500 text-white text-[11px] font-semibold shrink-0 mt-0.5">
                      {index + 1}
                    </span>
                    <p className="text-[13px] max-sm:text-xs text-[var(--text-primary)] leading-relaxed m-0">
                      {highlight.title}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Strategic Recommendations */}
            {currentTabData.strategicRecommendations?.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Lightbulb size={18} className="text-amber-500" />
                    <h2 className="text-lg font-semibold text-[var(--text-primary)] m-0">Strategic Recommendations</h2>
                  </div>
                  <button
                    onClick={() => copyAllToClipboard(currentTabData.strategicRecommendations || [], 'recommendations')}
                    className={`p-2 rounded-lg transition-colors ${
                      copiedSection === 'recommendations-success' ? 'text-green-500 bg-green-50 dark:bg-green-950/30' :
                      'text-[var(--text-tertiary)] hover:text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)]'
                    }`}
                    title="Copy all recommendations"
                  >
                    {copiedSection === 'recommendations-success' ? <Check size={16} /> : <Copy size={16} />}
                  </button>
                </div>
                <div className="flex flex-col gap-3">
                  {currentTabData.strategicRecommendations?.map((rec, index) => (
                    <div key={rec.id} className="flex items-start gap-4 bg-[var(--card-bg)] border border-[var(--border-color)] rounded-xl p-4 md:p-5 hover:shadow-md transition-shadow">
                      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-500 text-white text-sm font-semibold shrink-0">
                        {index + 1}
                      </span>
                      <p className="text-sm max-sm:text-xs text-[var(--text-primary)] leading-relaxed m-0 pt-1">
                        {rec.title}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 px-4 text-center text-[var(--text-secondary)]">
            <Info size={48} className="mb-3 opacity-40 max-sm:w-9 max-sm:h-9" />
            <h3 className="text-lg max-sm:text-sm font-medium text-[var(--text-primary)]">No reports have been configured for you yet.</h3>
            <p className="text-sm max-sm:text-xs">To get started, please request access from your analytics team or contact BeagleGPT support.</p>
          </div>
        )}

        {/* Full Report Section */}
        {currentTabData?.fullReportUrl?.length > 0 && (
          <>
            {isReportExpanded && (
              <div className="flex justify-end mb-3">
                <button
                  onClick={handleReadFullReport}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] border border-[var(--border-color)] rounded-lg hover:bg-[var(--bg-tertiary)] transition-colors"
                >
                  <ChevronUp size={16} />
                  Close Full Report
                </button>
              </div>
            )}

            {isReportExpanded && (
              <div ref={reportSectionRef} className="bg-[var(--card-bg)] border border-[var(--border-color)] rounded-xl overflow-hidden mb-6">
                <div className="flex items-center justify-between px-3 sm:px-5 py-3 border-b border-[var(--border-color)]">
                  <div className="flex items-center gap-2">
                    <BookOpen size={18} className="text-[var(--text-secondary)]" />
                    <h2 className="text-sm font-semibold text-[var(--text-primary)] m-0 uppercase tracking-wide">
                      Full Report - {activeTab?.replace(/_/g, '-')}
                    </h2>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={copyReportToClipboard}
                      className={`p-2 rounded-lg transition-colors ${
                        copiedSection === 'report-success'
                          ? 'text-green-500 bg-green-50 dark:bg-green-950/30'
                          : copiedSection === 'report-error'
                          ? 'text-red-500'
                          : 'text-[var(--text-tertiary)] hover:text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)]'
                      }`}
                      title="Copy report"
                    >
                      {copiedSection === 'report-success' ? <Check size={16} /> : <Copy size={16} />}
                    </button>
                    <button
                      onClick={handleReportTTS}
                      className="p-2 rounded-lg transition-all"
                      style={{
                        color: ttsState === 'playing' ? '#EF4444'
                             : ttsState === 'loading' ? 'var(--text-primary)'
                             : 'var(--text-tertiary)',
                        backgroundColor: ttsState === 'playing' ? 'rgba(239, 68, 68, 0.1)'
                                       : ttsState === 'loading' ? 'rgba(0, 0, 0, 0.05)'
                                       : 'transparent',
                      }}
                      title={ttsState === 'playing' ? 'Stop narration'
                           : ttsState === 'loading' ? 'Starting narration...'
                           : 'Narrate report'}
                    >
                      {ttsState === 'playing' ? <Square size={16} fill="currentColor" />
                       : ttsState === 'loading' ? <Loader2 size={16} className="animate-spin" />
                       : <Volume2 size={16} />}
                    </button>
                  </div>
                </div>
                <div className="flex gap-2 px-3 sm:px-5 py-3 border-b border-[var(--border-color)] overflow-x-auto">
                  {currentTabData.fullReportUrl.map((report, index) => (
                    <button
                      key={index}
                      onClick={() => handleReportTabChange(index)}
                      className={`px-3 py-1.5 rounded-lg text-sm whitespace-nowrap transition-colors ${
                        activeReportTab === index
                          ? 'bg-brand-500/10 text-brand-500 font-medium'
                          : 'text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)]'
                      }`}
                    >
                      {report.label}
                    </button>
                  ))}
                </div>
                <div ref={reportContentRef} className="p-2 sm:p-3 md:p-5 max-w-4xl mx-auto">
                  <StreamViewer
                    streamUrl={currentTabData.fullReportUrl[activeReportTab]?.url}
                    heading=""
                    status="done"
                    setStartResearch={setIsReportExpanded}
                    setStreamUrl={() => {}}
                    disableTTS
                  />
                </div>
              </div>
            )}

            {!isReportExpanded && (
              <div
                onClick={handleReadFullReport}
                className="flex items-center justify-center gap-2 py-4 bg-gradient-to-r from-brand-50 to-brand-100 dark:from-brand-900/20 dark:to-brand-800/20 rounded-xl cursor-pointer hover:shadow-md transition-all border border-brand-200 dark:border-brand-800/40 mb-6"
              >
                <FileText size={18} className="text-brand-600 dark:text-brand-400" />
                <span className="text-sm font-medium text-brand-700 dark:text-brand-300">Read Full Report</span>
                <ArrowRight size={16} className="text-brand-600 dark:text-brand-400" />
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      {currentTabData?.metadata && (
        <div className="sticky bottom-0 z-10 bg-[var(--bg-secondary)] border-t border-[var(--border-color)] px-2 sm:px-4 md:px-6 py-1.5"
          style={{ boxShadow: '0 -4px 12px rgba(0,0,0,0.08)' }}>

          {/* Desktop layout (sm+): text-based row */}
          <div className="hidden sm:flex items-center justify-between gap-4 text-xs">
            <div className="flex items-center gap-3 overflow-hidden min-w-0">
              <button
                onClick={() => setShowFrameworksModal(true)}
                className="flex items-center gap-1.5 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors shrink-0"
              >
                <Layers size={14} />
                <span>{currentTabData.metadata.analysisFrameworks?.count || 0} Analysis Frameworks</span>
                <Info size={12} className="opacity-50" />
              </button>

              <span className="text-[var(--border-color)]">|</span>

              <div className="flex items-center gap-1.5 overflow-hidden min-w-0">
                <Database size={14} className="text-[var(--text-secondary)] shrink-0" />
                <span className="text-[var(--text-secondary)] shrink-0">Sources:</span>
                <div className="flex items-center gap-2 overflow-hidden" ref={datasourcesItemsRef}>
                  {(visibleSourcesCount === null ? allSources : allSources.slice(0, visibleSourcesCount)).map((source) => (
                    <span key={source.id} data-source-item className="flex items-center gap-1 shrink-0">
                      <span className="w-2 h-2 rounded-full bg-[var(--success-dot)]" />
                      <span className="text-[var(--text-primary)] font-medium">{source.name}</span>
                      <span className="text-[var(--text-tertiary)]">as of: {source.dataAsOf}</span>
                    </span>
                  ))}
                </div>
                {(visibleSourcesCount === null || allSources.length > visibleSourcesCount) && (
                  <button
                    ref={sourcesExpandRef}
                    onClick={() => setShowSourcesModal(true)}
                    className="text-[var(--text-secondary)] hover:text-brand-500 transition-colors"
                    style={visibleSourcesCount === null ? { visibility: 'hidden' } : {}}
                  >
                    »
                  </button>
                )}
              </div>

              {currentTabData.metadata.totalRecordsProcessed > 0 && (
                <>
                  <span className="text-[var(--border-color)]">|</span>
                  <span className="flex items-center gap-1 text-[var(--text-secondary)] shrink-0">
                    <Search size={14} />
                    Scanned: <strong className="text-[var(--text-primary)]">{formatCount(currentTabData.metadata.totalRecordsProcessed)}</strong> records
                  </span>
                </>
              )}
            </div>

            {currentTabData.metadata.aiGenerated && (
              <div className="flex items-center gap-1 px-2.5 py-1 rounded-full border border-brand-200 dark:border-brand-800 text-brand-600 dark:text-brand-400 shrink-0">
                <Sparkles size={12} />
                <span className="font-medium">AI GENERATED</span>
              </div>
            )}
          </div>

          {/* Mobile layout (< sm): icon-only strip */}
          <div className="flex sm:hidden items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                ref={mobileFrameworksButtonRef}
                onClick={() => setShowFrameworksModal(true)}
                aria-label={`${currentTabData.metadata.analysisFrameworks?.count || 0} Analysis Frameworks`}
                className="flex items-center justify-center active:scale-95 transition-all text-[var(--text-secondary)]"
              >
                <Layers size={16} />
              </button>

              <span className="text-[var(--border-color)]">|</span>

              <button
                ref={mobileDatabaseButtonRef}
                onClick={() => setShowSourcesModal(true)}
                aria-label={`${allSources.length} data sources`}
                className="flex items-center justify-center active:scale-95 transition-all text-[var(--text-secondary)]"
              >
                <Database size={16} />
              </button>

              <span className="text-[var(--border-color)]">|</span>

              {currentTabData.metadata.totalRecordsProcessed > 0 && (
                <span className="flex items-center gap-1 text-xs text-[var(--text-secondary)] shrink-0">
                  Scanned <strong className="text-[var(--text-primary)]">{formatCount(currentTabData.metadata.totalRecordsProcessed)}</strong> records
                </span>
              )}
            </div>

            {currentTabData.metadata.aiGenerated && (
              <div
                className="flex items-center justify-center w-8 h-8 rounded-full border border-brand-200 dark:border-brand-800 text-brand-600 dark:text-brand-400"
                title="AI Generated"
              >
                <Sparkles size={14} />
              </div>
            )}
          </div>

        </div>
      )}

      {/* Modals */}
      <ExpandModal open={showFrameworksModal} onClose={() => setShowFrameworksModal(false)} title="APPLIED FRAMEWORKS" position="bottom-left">
        <div className="space-y-3">
          {currentTabData?.metadata?.analysisFrameworks?.items?.map((framework) => (
            <div key={framework.id} className="p-3 bg-[var(--bg-secondary)] rounded-lg">
              <h4 className="font-medium text-[var(--text-primary)] text-sm m-0">{framework.name}</h4>
              <p className="text-[var(--text-secondary)] text-xs mt-1 m-0">{framework.description}</p>
            </div>
          ))}
        </div>
      </ExpandModal>

      <ExpandModal open={showSourcesModal} onClose={() => setShowSourcesModal(false)} title="SOURCES" position="bottom-left" anchorElement={sourcesExpandRef.current}>
        <div className="space-y-2">
          {allSources.map((source) => (
            <div key={source.id} className="flex items-center gap-2 p-2">
              <span className="w-2 h-2 rounded-full bg-[var(--success-dot)]" />
              <span className="text-[var(--text-primary)] text-sm font-medium">{source.name}</span>
              <span className="text-[var(--text-tertiary)] text-xs">as of: {source.dataAsOf}</span>
            </div>
          ))}
        </div>
      </ExpandModal>

      <ExpandModal open={showMoreTabsModal} onClose={() => setShowMoreTabsModal(false)} title="MORE REPORTS" position="top-right" anchorElement={moreTabsButtonRef.current}>
        <div className="space-y-1">
          {hiddenTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => { handleTabChange(tab.key); setShowMoreTabsModal(false); }}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] rounded-lg transition-colors text-left"
            >
              <FileText size={14} className="text-[var(--text-secondary)]" />
              {tab.label}
            </button>
          ))}
        </div>
      </ExpandModal>
    </div>
  );
};

export default InsightsDashboard;
